import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  UserIcon, 
  LockClosedIcon,
  OfficeBuildingIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  PhoneIcon,
  BriefcaseIcon,
  ShoppingBagIcon,
  UserGroupIcon,
  LocationMarkerIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  CheckIcon
} from '@heroicons/react/outline';
import LocationPicker from '../components/LocationPicker';
import { signInWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '../firebase-config';

const SignUp = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const location = useLocation();
  
  // Form data
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    businessName: '',
    phoneNumber: '',
    userType: 'supplier', // Always supplier
    businessCategory: [], // Changed to array for multiple categories
    location: '',
    locationData: null // New field for detailed location data
  });
  
  const { user, userProfile, signInWithGoogle, getDashboardPath } = useAuth();
  const navigate = useNavigate();

  // Handle URL parameters for user type
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const userType = urlParams.get('type');
    if (userType && (userType === 'buyer' || userType === 'supplier')) {
      setFormData(prev => ({ ...prev, userType }));
    }
  }, [location]);

  // Redirect authenticated users with complete profiles
  useEffect(() => {
    console.log('🔍 Redirect useEffect triggered:', { 
      user: !!user, 
      userProfile: !!userProfile, 
      profileComplete: userProfile?.profileComplete, 
      step 
    });
    
    if (user && userProfile?.profileComplete && step === 1) {
      console.log('🔍 Redirecting to dashboard - user has complete profile');
      navigate(getDashboardPath());
    }
  }, [user, userProfile, navigate]);

  // Fetch categories from backend API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch('https://us-central1-twerceapp.cloudfunctions.net/getCategories');
        const data = await response.json();
        
        if (data.success && data.categories) {
          setCategories(data.categories);
        } else {
          console.error('Failed to fetch categories:', data.error);
          setError('Failed to load categories. Please refresh the page.');
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        setError('Failed to load categories. Please refresh the page.');
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Enable signup when component mounts
  useEffect(() => {
    // This will be handled by the form submission
    console.log('🔍 SignUp component mounted');
  }, []);

  const updateFormData = (field, value) => {
    console.log(`🔍 updateFormData called: ${field} = ${value}`);
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    console.log(`🔍 nextStep called, current step: ${step}, formData:`, formData);
    if (validateStep()) {
      setStep(step + 1);
      setError('');
    }
  };

  const prevStep = () => {
    setStep(step - 1);
    setError('');
  };

  const validateStep = () => {
    console.log(`🔍 validateStep called for step ${step}`);
    switch (step) {
      case 1:
        if (!formData.email || !formData.password || !formData.confirmPassword) {
          setError('Please fill in all fields');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          return false;
        }
        if (formData.password.length < 6) {
          setError('Password must be at least 6 characters');
          return false;
        }
        break;
      case 2:
        if (!formData.fullName || !formData.businessName || !formData.phoneNumber) {
          setError('Please fill in all fields');
          return false;
        }
        break;
      case 3:
        console.log(`🔍 Validating step 3: businessCategory=${formData.businessCategory}`);
        if (formData.businessCategory.length === 0) {
          setError('Please select at least one business category');
          return false;
        }
        break;
      case 4:
        // Validate location for all suppliers
        console.log('🔍 Validating step 4: locationData=', formData.locationData);
        if (!formData.locationData || !formData.locationData.name || !formData.locationData.latitude || !formData.locationData.longitude) {
          setError('Please select your business location and set your service radius');
          return false;
        }
        if (!formData.locationData.radius || formData.locationData.radius < 5) {
          setError('Please set a service radius of at least 5km');
          return false;
        }
        break;
      default:
        return true;
    }
    return true;
  };

  const handleLocationChange = (locationData) => {
    console.log('🔍 handleLocationChange called with:', locationData);
    // Include the current radius in the location data
    const locationDataWithRadius = {
      ...locationData,
      radius: formData.locationData?.radius || 50
    };
    console.log('🔍 Updated location data with radius:', locationDataWithRadius);
    updateFormData('locationData', locationDataWithRadius);
    if (locationData?.name) {
      updateFormData('location', locationData.name);
    }
  };

  const handleSubmit = async (e) => {
    console.log('🔍 handleSubmit called - form submission triggered');
    console.log('🔍 Event details:', { 
      type: e.type, 
      target: e.target?.tagName, 
      currentTarget: e.currentTarget?.tagName,
      step,
      formData: {
        email: !!formData.email,
        password: !!formData.password,
        fullName: !!formData.fullName,
        businessName: !!formData.businessName,
        phoneNumber: !!formData.phoneNumber,
        businessCategory: formData.businessCategory?.length,
        locationData: !!formData.locationData
      }
    });
    e.preventDefault();
    
    // Final validation for all required fields before submission
    const requiredFields = [
      'email',
      'password',
      'fullName',
      'businessName',
      'phoneNumber',
      'userType',
      'businessCategory'
    ];
    for (const field of requiredFields) {
      if (field === 'businessCategory') {
        if (!formData[field] || formData[field].length === 0) {
          setError('Please select at least one business category before submitting.');
          return;
        }
      } else if (!formData[field]) {
        setError('Please fill in all required fields before submitting.');
        return;
      }
    }
    
    // Prevent submission if not on the final step
    const finalStep = 4; // Always 4 steps for suppliers
    if (step !== finalStep) {
      console.log(`🔍 Form submission prevented - current step: ${step}, final step: ${finalStep}`);
      return;
    }
    
    // Additional validation for location data on final step
    if (!formData.locationData || !formData.locationData.name || !formData.locationData.latitude || !formData.locationData.longitude) {
      setError('Please select your business location before submitting');
      console.log('🔍 Form submission prevented - incomplete location data');
      return;
    }
    if (!formData.locationData.radius || formData.locationData.radius < 5) {
      setError('Please set a service radius of at least 5km before submitting');
      console.log('🔍 Form submission prevented - invalid radius');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log('🔍 About to call signUp function with:', { email: formData.email, userType: formData.userType });
      
      // Call the backend directly instead of using the AuthContext signUp function
      const response = await fetch('https://us-central1-twerceapp.cloudfunctions.net/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          userData: formData
        })
      });

      const result = await response.json();
      console.log('🔍 Backend signup result:', result);
      
      if (result.success) {
        // Sign in the user with Firebase Auth on the client side
        const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
        const user = userCredential.user;

        // Update profile with display name
        await updateProfile(user, {
          displayName: formData.fullName
        });

        setSuccess('Account created successfully! Redirecting...');
        setTimeout(() => {
          navigate(getDashboardPath());
        }, 2000);
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
      console.error('SignUp error:', error);
    }
    
    setLoading(false);
  };

  const handleGoogleSignUp = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    const result = await signInWithGoogle();
    
    if (result.success) {
      setSuccess('Google sign-up successful! Redirecting...');
      setTimeout(() => {
        navigate(getDashboardPath());
      }, 1500);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full mb-4">
          <UserIcon className="h-8 w-8 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Create Your Account</h3>
        <p className="text-gray-600">Start by setting up your login credentials</p>
      </div>

      <div className="space-y-5">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <UserIcon className="h-5 w-5 text-gray-400 group-focus-within:text-orange-500 transition-colors" />
            </div>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => updateFormData('email', e.target.value)}
              className="block w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 bg-white shadow-sm"
              placeholder="Enter your email"
            />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
            Password
          </label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <LockClosedIcon className="h-5 w-5 text-gray-400 group-focus-within:text-orange-500 transition-colors" />
            </div>
            <input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => updateFormData('password', e.target.value)}
              className="block w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 bg-white shadow-sm"
              placeholder="Create a password"
            />
          </div>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
            Confirm Password
          </label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <LockClosedIcon className="h-5 w-5 text-gray-400 group-focus-within:text-orange-500 transition-colors" />
            </div>
            <input
              id="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => updateFormData('confirmPassword', e.target.value)}
              className="block w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 bg-white shadow-sm"
              placeholder="Confirm your password"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mb-4">
          <OfficeBuildingIcon className="h-8 w-8 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Business Information</h3>
        <p className="text-gray-600">Tell us about your business</p>
      </div>

      <div className="space-y-5">
        <div>
          <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
            Full Name
          </label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <UserIcon className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            </div>
            <input
              id="fullName"
              type="text"
              value={formData.fullName}
              onChange={(e) => updateFormData('fullName', e.target.value)}
              className="block w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm"
              placeholder="Enter your full name"
            />
          </div>
        </div>

        <div>
          <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-2">
            Business Name
          </label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <OfficeBuildingIcon className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            </div>
            <input
              id="businessName"
              type="text"
              value={formData.businessName}
              onChange={(e) => updateFormData('businessName', e.target.value)}
              className="block w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm"
              placeholder="Enter your business name"
            />
          </div>
        </div>

        <div>
          <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number
          </label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <PhoneIcon className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            </div>
            <input
              id="phoneNumber"
              type="tel"
              value={formData.phoneNumber}
              onChange={(e) => updateFormData('phoneNumber', e.target.value)}
              className="block w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm"
              placeholder="Enter your phone number"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-500 to-teal-500 rounded-full mb-4">
          <BriefcaseIcon className="h-8 w-8 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Business Categories</h3>
        <p className="text-gray-600">Select all categories that apply to your business</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Business Categories
        </label>
        <div className="grid grid-cols-1 gap-3 max-h-60 overflow-y-auto border border-gray-200 rounded-xl p-4 bg-gray-50">
          {loadingCategories ? (
            // Loading skeleton
            Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="flex items-center p-3 bg-white rounded-lg border-2 border-gray-200 animate-pulse"
              >
                <div className="w-6 h-6 bg-gray-200 rounded mr-3"></div>
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                </div>
              </div>
            ))
          ) : (
                        categories.map((category) => (
              <label key={category.id} className="relative flex items-center p-3 bg-white rounded-lg border-2 border-gray-200 cursor-pointer hover:border-orange-300 hover:bg-orange-50 transition-all duration-200 group">
                <input
                  type="checkbox"
                  value={category.name}
                  checked={formData.businessCategory.includes(category.name)}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (e.target.checked) {
                      updateFormData('businessCategory', [...formData.businessCategory, value]);
                    } else {
                      updateFormData('businessCategory', formData.businessCategory.filter(cat => cat !== value));
                    }
                  }}
                  className="sr-only"
                />
                <div className={`flex items-center justify-center w-6 h-6 rounded border-2 mr-3 transition-all duration-200 ${
                  formData.businessCategory.includes(category.name)
                    ? 'bg-orange-500 border-orange-500'
                    : 'border-gray-300 group-hover:border-orange-400'
                }`}>
                  {formData.businessCategory.includes(category.name) && (
                    <CheckIcon className="w-4 h-4 text-white" />
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{category.icon || "📦"}</span>
                  <span className="text-sm font-medium text-gray-700">{category.name}</span>
                </div>
              </label>
            ))
          )}
        </div>
        {formData.businessCategory.length > 0 && (
          <div className="mt-4 p-4 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl">
            <div className="flex items-center space-x-2">
              <CheckCircleIcon className="h-5 w-5 text-orange-600" />
              <p className="text-sm font-medium text-orange-800">
                Selected {formData.businessCategory.length} categor{formData.businessCategory.length === 1 ? 'y' : 'ies'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep4 = () => {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mb-4">
            <LocationMarkerIcon className="h-8 w-8 text-white" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Set Your Service Area</h3>
          <p className="text-gray-600">Choose your business location and service radius to receive relevant RFQs</p>
        </div>

        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6">
          <LocationPicker
            onLocationSelect={handleLocationChange}
            selectedLocation={formData.locationData}
            showRadius={true}
            radius={formData.locationData?.radius || 50}
            onRadiusChange={(radius) => {
              if (formData.locationData) {
                updateFormData('locationData', { ...formData.locationData, radius });
              }
            }}
            placeholder="Search for your business location..."
          />

          {formData.locationData ? (
            <div className="mt-6 p-4 bg-white border border-purple-200 rounded-lg shadow-sm">
              <div className="flex items-center space-x-2 mb-2">
                <LocationMarkerIcon className="h-5 w-5 text-purple-600" />
                <h4 className="font-medium text-purple-800">Service Area Summary</h4>
              </div>
              <p className="text-sm text-purple-700">
                You'll receive RFQs from buyers within a <strong>{formData.locationData.radius || 50}km radius</strong> of <strong>{formData.locationData.name}</strong>.
              </p>
            </div>
          ) : (
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <LocationMarkerIcon className="h-5 w-5 text-yellow-600" />
                <h4 className="font-medium text-yellow-800">Location Required</h4>
              </div>
              <p className="text-sm text-yellow-700">
                Please select your business location and set your service radius to complete your registration.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-20 w-20 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mb-6 shadow-lg">
            <OfficeBuildingIcon className="h-10 w-10 text-white" />
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-3">
            Create Supplier Account
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Step {step} of 4 - Join the B2B marketplace as a supplier
          </p>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
          <div 
            className="bg-gradient-to-r from-orange-500 to-red-500 h-3 rounded-full transition-all duration-500 ease-out shadow-sm"
            style={{ width: `${(step / 4) * 100}%` }}
          ></div>
        </div>

        {/* Sign Up Form */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center space-x-2">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-red-700 text-sm font-medium">{error}</p>
              </div>
            </div>
          )}
          
          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center space-x-2">
                <CheckCircleIcon className="h-5 w-5 text-green-600" />
                <p className="text-green-700 text-sm font-medium">{success}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              console.log('🔍 Enter key pressed - preventing form submission');
            }
          }}>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-10">
              {step > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex items-center px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium shadow-sm"
                >
                  <ChevronLeftIcon className="h-5 w-5 mr-2" />
                  Back
                </button>
              )}
              
              {step < 4 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="ml-auto flex items-center px-8 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Next
                  <ChevronRightIcon className="h-5 w-5 ml-2" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className="ml-auto flex items-center px-8 py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-xl hover:from-green-600 hover:to-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Creating Account...
                    </>
                  ) : (
                    <>
                      Create Supplier Account
                      <CheckIcon className="h-5 w-5 ml-2" />
                    </>
                  )}
                </button>
              )}
            </div>
          </form>

          {/* Divider */}
          <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500 font-medium">Or continue with</span>
              </div>
            </div>
          </div>

          {/* Google Sign In Button */}
          <div className="mt-6">
            <button
              type="button"
              onClick={handleGoogleSignUp}
              disabled={loading}
              className="w-full inline-flex justify-center items-center py-3 px-4 border border-gray-300 rounded-xl shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
          </div>

          {/* Sign In Link */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium text-orange-600 hover:text-orange-700 transition-colors"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignUp; 